import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../../../lib/api';
import { CommessaDettaglio } from '../SchedaCommessa';
import { getRuoloLabel } from '../../../../utils/ruoliLabel';

// ── Tipi ─────────────────────────────────────────────────────────────────────

interface MembroTeam {
  id: number;
  utenteId: number;
  nomeCompleto: string;
  email: string;
  ruolo: string;
  aggiuntoAt: string;
  aggiuntoById: number;
  aggiuntoByNome?: string;
}

interface Utente {
  id: number;
  nome: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—';

// ── Componente ────────────────────────────────────────────────────────────────

export default function TabTeam({
  commessa, onRefresh,
}: {
  commessa: CommessaDettaglio;
  onRefresh: () => void;
}) {
  const [team,    setTeam]    = useState<MembroTeam[]>([]);
  const [utenti,  setUtenti]  = useState<Utente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // Modal aggiungi
  const [modal, setModal]       = useState(false);
  const [selUtente, setSelUtente] = useState('');
  const [saving, setSaving]     = useState(false);
  const [modalErr, setModalErr] = useState('');

  // ── Carica ─────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [tRes, uRes] = await Promise.all([
        apiFetch<{ team: MembroTeam[] }>(`/api/commesse/${commessa.id}/team`),
        // @api GET /api/utenti/selezionabili?societaId={id} -> {"success":"true","data":[{"id":"number","nome":"string"}]}
        apiFetch<{ success: boolean; data: Utente[] }>(`/api/utenti/selezionabili?societaId=${commessa.societaId}`),
      ]);
      setTeam(tRes.team ?? []);
      setUtenti(uRes.data ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [commessa.id, commessa.societaId]);

  useEffect(() => { load(); }, [load]);

  // ── Aggiungi ────────────────────────────────────────────────────────────────

  const apriAggiungi = () => {
    setSelUtente(''); setModalErr(''); setModal(true);
  };

  const aggiungi = async () => {
    if (!selUtente) { setModalErr('Seleziona un utente'); return; }
    setSaving(true); setModalErr('');
    try {
      await apiFetch(`/api/commesse/${commessa.id}/team`, {
        method: 'POST',
        body: JSON.stringify({ utenteId: Number(selUtente) }),
      });
      setModal(false); load(); onRefresh();
    } catch (e: any) { setModalErr(e.message); }
    finally { setSaving(false); }
  };

  // ── Rimuovi ─────────────────────────────────────────────────────────────────

  const rimuovi = async (m: MembroTeam) => {
    if (!window.confirm(`Rimuovere ${m.nomeCompleto} dal team?`)) return;
    try {
      await apiFetch(`/api/commesse/${commessa.id}/team/${m.utenteId}`, { method: 'DELETE' });
      load(); onRefresh();
    } catch (e: any) { setError(e.message); }
  };

  // Utenti non ancora nel team (escludi responsabile e team esistente)
  const teamIds = new Set([commessa.responsabileId, ...team.map(m => m.utenteId)]);
  const disponibili = utenti.filter(u => !teamIds.has(u.id));

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="bop-flex-between bop-tab-team__header">
        <span className="bop-tab-team__count">
          {team.length + 1} membro{team.length !== 0 ? 'i' : ''} (incluso responsabile)
        </span>
        <button className="bop-btn bop-btn-primary" onClick={apriAggiungi}>+ Aggiungi al Team</button>
      </div>

      {error && <div className="inline-error">{error}</div>}

      {loading ? (
        <div className="inline-loading">Caricamento team…</div>
      ) : (
        <div className="bop-tab-team__list">
          {/* Card Responsabile */}
          <div className="bop-tab-team__card-responsabile">
            <Avatar nome={commessa.responsabileNome ?? '?'} size={48} />
            <div className="bop-tab-team__card-body">
              <div className="bop-tab-team__card-name-row">
                <span className="bop-tab-team__card-name-resp">{commessa.responsabileNome}</span>
                <span className="bop-badge bop-badge--info bop-tab-team__badge-resp">Responsabile</span>
              </div>
              <div className="bop-tab-team__card-email">{commessa.responsabileNome}</div>
            </div>
          </div>

          {/* Membri */}
          {team.length === 0 ? (
            <div className="bop-tab-team__empty">
              Nessun membro aggiuntivo nel team
            </div>
          ) : (
            team.map(m => (
              <div key={m.id} className="bop-tab-team__card-membro">
                <Avatar nome={m.nomeCompleto} size={40} />
                <div className="bop-tab-team__card-body">
                  <div className="bop-tab-team__membro-name">{m.nomeCompleto}</div>
                  <div className="bop-tab-team__membro-email">{m.email}</div>
                  <div className="bop-tab-team__membro-meta">
                    <span className="bop-badge bop-badge--neutral bop-tab-team__badge-ruolo">
                      {getRuoloLabel(m.ruolo)}
                    </span>
                    Aggiunto{m.aggiuntoByNome ? ` da ${m.aggiuntoByNome}` : ''} il {fmtDate(m.aggiuntoAt)}
                  </div>
                </div>
                <button className="bop-btn bop-btn-danger bop-btn-ghost bop-btn-sm" onClick={() => rimuovi(m)}>
                  Rimuovi
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Modal Aggiungi ── */}
      {modal && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--sm">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Aggiungi al Team</h3>
              <button className="bop-modal__close"
                onClick={() => setModal(false)}>×</button>
            </div>
            <div className="bop-modal__body">
              {modalErr && <div className="inline-error">{modalErr}</div>}
              <div className="bop-form-group">
                <label className="bop-label">Utente <span className="bop-text-danger">*</span></label>
                {disponibili.length === 0 ? (
                  <div className="bop-tab-team__no-utenti">
                    Tutti gli utenti della società sono già nel team.
                  </div>
                ) : (
                  <select className="bop-input" value={selUtente}
                    onChange={e => setSelUtente(e.target.value)}>
                    <option value="">Seleziona un utente…</option>
                    {disponibili.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.nome}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModal(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={aggiungi} disabled={saving || disponibili.length === 0}>
                {saving ? 'Aggiunta…' : 'Aggiungi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────────────────────

function Avatar({ nome, size }: { nome: string; size: number }) {
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#0ea5e9'];
  let h = 0;
  for (let i = 0; i < nome.length; i++) h = nome.charCodeAt(i) + ((h << 5) - h);
  const color = colors[Math.abs(h) % colors.length];
  const ini = nome.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();
  /* Avatar uses dynamic computed color and size — inline style justified */
  return (
    <div className="bop-tab-team__avatar" style={{
      width: size, height: size, background: color, fontSize: size * 0.36,
    }}>
      {ini}
    </div>
  );
}
